/**
 * TableDialogs — all modal dialogs for table management.
 * Receives state + handlers from TableDetail (via useTableActions).
 * Payment dialog extracted to TablePaymentDialog.js.
 */
import { useTranslation } from "react-i18next";
import { formatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TablePaymentDialog } from "./TablePaymentDialog";

export function TableDialogs({
  // Dialog visibility
  showCancel, setShowCancel,
  showSplit, setShowSplit,
  showTransferItem, setShowTransferItem,
  showTransferAll, setShowTransferAll,
  showPayment, resetPaymentState,
  showDiscount, setShowDiscount,
  // Shared context
  tableName, otherTables, allItems, total,
  // Cancel
  handleCancel,
  // Split
  splitMode, setSplitMode, splitPersons, setSplitPersons,
  splitResult, setSplitResult, selectedSplitItems, toggleSplitItem, handleSplitCalc,
  // Transfer
  selectedTransferItems, toggleTransferItem,
  targetTableId, setTargetTableId,
  handleTransferItems, handleTransferAll,
  // Payment (passed through to TablePaymentDialog)
  paymentMode, setPaymentMode, partialPayments, handleRemovePartialPayment,
  remainingToPay, finalTotal, totalPartialPaid, appliedDiscount,
  currentPartialAmount, setCurrentPartialAmount,
  paymentMethod, setPaymentMethod, cardType, setCardType,
  cashReceived, setCashReceived, needsChange, setNeedsChange, changeAmount, partialCashChange,
  handleAddPartialPayment, processingPayment, handlePayment,
  // Discount
  discountType, setDiscountType, discountValue, setDiscountValue, handleApplyDiscount,
}) {
  const { t } = useTranslation();

  return (
    <>
      {/* === CANCEL MESA DIALOG === */}
      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="uppercase font-bold text-red-600">Cancelar Mesa</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Los pedidos abiertos serán cancelados. Los registros se guardan y nunca se eliminan.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancel(false)}>Volver</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleCancel} data-testid="confirm-cancel-btn">Confirmar Cancelacion</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === SPLIT BILL DIALOG === */}
      <Dialog open={showSplit} onOpenChange={(open) => { setShowSplit(open); if (!open) setSplitResult(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="uppercase font-bold">Dividir Cuenta</DialogTitle></DialogHeader>
          <div className="flex gap-2 mb-4">
            <button className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${splitMode === "persons" ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-600"}`} onClick={() => { setSplitMode("persons"); setSplitResult(null); }} data-testid="split-by-persons">Por Personas</button>
            <button className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${splitMode === "items" ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-600"}`} onClick={() => { setSplitMode("items"); setSplitResult(null); }} data-testid="split-by-items">Por Items</button>
          </div>
          {splitMode === "persons" ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Total: <strong>{formatPrice(total)}</strong></p>
              <div className="flex items-center gap-3">
                <Label>Personas:</Label>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setSplitPersons(Math.max(2, splitPersons - 1))}>-</Button>
                  <span className="text-xl font-bold w-8 text-center">{splitPersons}</span>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setSplitPersons(splitPersons + 1)}>+</Button>
                </div>
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleSplitCalc} data-testid="calc-split-btn">Calcular</Button>
              {splitResult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Cada persona paga:</p>
                  <p className="text-3xl font-black text-emerald-700">{formatPrice(splitResult.per_person)}</p>
                  <p className="text-xs text-gray-400 mt-1">{splitResult.num_persons} personas - Total: {formatPrice(splitResult.total)}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Selecciona los items del cliente:</p>
              <div className="space-y-1 max-h-60 overflow-y-auto border rounded-lg p-2">
                {allItems.map((item, idx) => {
                  const isSelected = selectedSplitItems.some(s => s.order_id === item.order_id && s.item_index === item.item_index);
                  return (
                    <label key={idx} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition ${isSelected ? "bg-emerald-50 border border-emerald-200" : "hover:bg-gray-50"}`}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSplitItem(item.order_id, item.item_index)} className="rounded" />
                      <span className="text-sm flex-1">{item.quantity}x {item.product_name}</span>
                      <span className="text-sm font-bold">{formatPrice(item.quantity * item.unit_price)}</span>
                    </label>
                  );
                })}
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleSplitCalc} disabled={selectedSplitItems.length === 0} data-testid="calc-split-items-btn">Calcular Subtotal</Button>
              {splitResult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Subtotal seleccionado:</p>
                  <p className="text-3xl font-black text-emerald-700">{formatPrice(splitResult.subtotal)}</p>
                  <p className="text-xs text-gray-400 mt-1">{splitResult.items?.length || 0} items</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === TRANSFER ITEM DIALOG === */}
      <Dialog open={showTransferItem} onOpenChange={setShowTransferItem}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="uppercase font-bold">Transferir Items</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600 mb-3">Selecciona items y la mesa destino:</p>
          <div className="space-y-1 max-h-48 overflow-y-auto border rounded-lg p-2 mb-3">
            {allItems.map((item, idx) => {
              const isSelected = selectedTransferItems.some(s => s.order_id === item.order_id && s.item_index === item.item_index);
              return (
                <label key={idx} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition ${isSelected ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50"}`}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggleTransferItem(item.order_id, item.item_index)} className="rounded" />
                  <span className="text-sm flex-1">{item.quantity}x {item.product_name}</span>
                  <span className="text-sm font-bold">{formatPrice(item.quantity * item.unit_price)}</span>
                </label>
              );
            })}
            {allItems.length === 0 && <p className="text-center text-gray-400 py-4 text-sm">Sin items</p>}
          </div>
          <div className="space-y-2">
            <Label>Mesa destino:</Label>
            <select value={targetTableId} onChange={e => setTargetTableId(e.target.value)} className="w-full border rounded-lg p-2 text-sm" data-testid="target-table-select">
              <option value="">Seleccionar mesa...</option>
              {otherTables.map(t => <option key={t.id} value={t.id}>{t.name || `MESA ${String(t.number).padStart(2,"0")}`}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferItem(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleTransferItems} disabled={!targetTableId || selectedTransferItems.length === 0} data-testid="confirm-transfer-items-btn">Transferir {selectedTransferItems.length} items</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === TRANSFER ALL DIALOG === */}
      <Dialog open={showTransferAll} onOpenChange={setShowTransferAll}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="uppercase font-bold">Transferir Mesa Completa</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Todos los items de <strong>{tableName}</strong> serán movidos a otra mesa.</p>
          <div className="space-y-2 mt-3">
            <Label>Mesa destino:</Label>
            <select value={targetTableId} onChange={e => setTargetTableId(e.target.value)} className="w-full border rounded-lg p-2 text-sm" data-testid="target-table-all-select">
              <option value="">Seleccionar mesa...</option>
              {otherTables.map(t => <option key={t.id} value={t.id}>{t.name || `MESA ${String(t.number).padStart(2,"0")}`}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferAll(false)}>Cancelar</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleTransferAll} disabled={!targetTableId} data-testid="confirm-transfer-all-btn">Transferir Todo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === PAYMENT DIALOG === */}
      <TablePaymentDialog
        showPayment={showPayment}
        resetPaymentState={resetPaymentState}
        paymentMode={paymentMode}
        setPaymentMode={setPaymentMode}
        partialPayments={partialPayments}
        handleRemovePartialPayment={handleRemovePartialPayment}
        remainingToPay={remainingToPay}
        finalTotal={finalTotal}
        totalPartialPaid={totalPartialPaid}
        appliedDiscount={appliedDiscount}
        currentPartialAmount={currentPartialAmount}
        setCurrentPartialAmount={setCurrentPartialAmount}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        cardType={cardType}
        setCardType={setCardType}
        cashReceived={cashReceived}
        setCashReceived={setCashReceived}
        needsChange={needsChange}
        setNeedsChange={setNeedsChange}
        changeAmount={changeAmount}
        partialCashChange={partialCashChange}
        handleAddPartialPayment={handleAddPartialPayment}
        processingPayment={processingPayment}
        handlePayment={handlePayment}
      />

      {/* === DISCOUNT DIALOG === */}
      <Dialog open={showDiscount} onOpenChange={setShowDiscount}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="uppercase font-bold flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2v16z" />
            </svg>
            Aplicar Desconto
          </DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600">Total da Conta</p>
              <p className="text-2xl font-bold text-gray-900">{formatPrice(total)}</p>
            </div>
            <div className="space-y-2">
              <Label className="font-bold">Tipo de Desconto</Label>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setDiscountType("percentage")} className={`p-3 rounded-lg border-2 font-bold transition ${discountType === "percentage" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-gray-200"}`} data-testid="discount-type-percentage">% Percentual</button>
                <button onClick={() => setDiscountType("fixed")} className={`p-3 rounded-lg border-2 font-bold transition ${discountType === "fixed" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-gray-200"}`} data-testid="discount-type-fixed">€ Valor Fixo</button>
              </div>
            </div>
            <div>
              <Label className="font-bold">{discountType === "percentage" ? "Percentual (%)" : "Valor (€)"}</Label>
              <Input type="number" step={discountType === "percentage" ? "1" : "0.01"} min="0" max={discountType === "percentage" ? "100" : total} value={discountValue} onChange={e => setDiscountValue(e.target.value)} className="mt-1 text-lg font-bold text-center" placeholder={discountType === "percentage" ? "10" : "5.00"} data-testid="discount-value-input" />
            </div>
            {discountValue && parseFloat(discountValue) > 0 && (
              <div className="bg-amber-50 rounded-lg p-4 text-center">
                <p className="text-sm text-amber-700">Desconto a aplicar</p>
                <p className="text-xl font-black text-amber-800">-{formatPrice(discountType === "percentage" ? (total * parseFloat(discountValue)) / 100 : parseFloat(discountValue))}</p>
                <p className="text-sm text-gray-600 mt-2">Novo total: <strong>{formatPrice(total - (discountType === "percentage" ? (total * parseFloat(discountValue)) / 100 : parseFloat(discountValue)))}</strong></p>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDiscount(false)}>Cancelar</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white font-bold" onClick={handleApplyDiscount} disabled={!discountValue || parseFloat(discountValue) <= 0} data-testid="confirm-discount-btn">Aplicar Desconto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
